import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '~/components/shadcn/rezisable.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { getInstrumentFinancialsPageDataQueryOptions } from '~/server/instrument/instrument-financials-page-data-query.ts';
import { cn } from '~/utils/cn.ts';
import { useWindowDimensions } from '~/utils/hooks.ts';
import { FinancialsChart } from '~/views/instrument/components/financials-chart.tsx';
import { FinancialsTable } from '~/views/instrument/components/financials-table.tsx';
import type { FinancialViewType } from '~/views/instrument/types/financial-types.ts';

export const Route = createFileRoute('/instrument/$ticker/financials')({
	component: InstrumentFinancialsPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(getInstrumentFinancialsPageDataQueryOptions({ ticker: params.ticker })),
	pendingComponent: InstrumentFinancialsPagePending,
	pendingMinMs: 250,
});

const initialSelectedChartKeys = new Map([
	['0.0', true],
	['0.4', true],
	['0.10', true],
]);

function calculateVerticalLayout({ height, width }: { readonly height: number | null; readonly width: number | null }) {
	if (!height || !width) {
		return false;
	}

	if (width > 1800 || height < 900) {
		return true;
	}

	return false;
}

function InstrumentFinancialsPagePending() {
	return (
		<div className="h-full w-body-margin-width">
			<div className="flex h-full flex-col gap-2 p-2 lg:flex-row">
				<div className="flex min-h-[420px] flex-1 flex-col gap-2">
					<Skeleton className="h-12 w-full rounded-md border border-border bg-card" />
					<Skeleton className="h-full min-h-[360px] w-full rounded-md border border-border bg-card" />
				</div>
				<div className="flex min-h-[420px] flex-1 flex-col gap-2">
					<Skeleton className="h-12 w-full rounded-md border border-border bg-card" />
					<Skeleton className="h-full min-h-[360px] w-full rounded-md border border-border bg-card" />
				</div>
			</div>
		</div>
	);
}

function InstrumentFinancialsPage() {
	const t = useTranslations();
	const { ticker } = Route.useParams();
	const { data } = useSuspenseQuery(getInstrumentFinancialsPageDataQueryOptions({ ticker }));
	const { formattedFinancials, formatting: initialFormatting, userLocale } = data;
	const [toggledView, setToggledView] = useState<FinancialViewType>('yearly');
	const [selectedKeys, setSelectedKeys] = useState(initialSelectedChartKeys);
	const [formatting, setFormatting] = useState(initialFormatting ?? undefined);
	const id = useId();
	const { height, width } = useWindowDimensions();
	const [isHorizontal, setIsHorizontal] = useState(calculateVerticalLayout({ height, width }));

	useEffect(() => {
		setIsHorizontal(calculateVerticalLayout({ height, width }));
	}, [width, height]);

	if (!formattedFinancials || !formatting) {
		return (
			<div className="mx-auto flex">
				<div className="flex flex-col">
					<span className="mt-40 text-foreground italic">{t('instrument.noFinancials')}</span>
					<span className="text-foreground text-sm italic">(?) info@eqity.app</span>
				</div>
			</div>
		);
	}

	return (
		<div className="h-full w-body-margin-width">
			<ResizablePanelGroup direction={isHorizontal ? 'horizontal' : 'vertical'} id={id}>
				<ResizablePanel className={cn(isHorizontal ? 'h-full w-1/2' : 'h-1/2 w-full')}>
					<FinancialsTable
						columnKeys={formattedFinancials.columnKeys}
						formatting={formatting}
						rows={formattedFinancials.rows}
						selectedKeys={selectedKeys}
						setFormatting={setFormatting}
						setSelectedKeys={setSelectedKeys}
						setToggledView={setToggledView}
						toggledView={toggledView}
						userLocale={userLocale}
					/>
				</ResizablePanel>
				<ResizableHandle className="border-none" withHandle={true} />
				<ResizablePanel className={cn(isHorizontal ? 'h-full w-1/2' : 'h-1/2 w-full')}>
					<FinancialsChart
						formatting={formatting}
						rows={formattedFinancials.rows}
						selectedKeys={selectedKeys}
						toggledView={toggledView}
						userLocale={userLocale}
					/>
				</ResizablePanel>
			</ResizablePanelGroup>
		</div>
	);
}
